import React, { useCallback } from "react";
import { Pagination, PaginationItem, PaginationLink } from "reactstrap";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { Icon } from "./Icon/Icon";

type TablePaginationProps = {
  pagesCount: number;
  currentPage: number;
  pagesAround?: number;
  onChangePage: (page: number) => void;
};

const TablePaginationComp = Scrivito.connect(
  ({
    pagesCount,
    currentPage,
    pagesAround = 2,
    onChangePage,
  }: TablePaginationProps) => {
    const paginationItems: number[] = [];
    let iPage = currentPage - pagesAround;
    const lastShownPage = currentPage + pagesAround;
    for (iPage; iPage <= lastShownPage; iPage += 1) {
      if (iPage > 0 && iPage <= pagesCount) {
        paginationItems.push(iPage);
      }
    }

    const isDisabled = useCallback(
      (pageNum: number) => pageNum < 1 || pageNum > pagesCount,
      [pagesCount],
    );

    const handlePageChange = (pageNum: number): void => {
      if (!isDisabled(pageNum)) {
        onChangePage(pageNum);
      }
    };

    const renderEllipsis = useCallback(
      () => (
        <PaginationItem disabled>
          <PaginationLink>
            <Icon iconName="ellipsis-vertical-regular" className="rotate-90" />
          </PaginationLink>
        </PaginationItem>
      ),
      [],
    );

    return (
      <Pagination
        aria-label="My projects table navigation"
        className={cx({ invisible: pagesCount === 1 })}
      >
        <PaginationItem
          onClick={() => currentPage !== 1 && handlePageChange(1)}
          disabled={isDisabled(currentPage - 1)}
        >
          <PaginationLink>
            <Icon iconName="double-chevron-left-regular" />
          </PaginationLink>
        </PaginationItem>
        <PaginationItem
          disabled={isDisabled(currentPage - 1)}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          <PaginationLink>
            <Icon iconName="chevron-left-regular" />
          </PaginationLink>
        </PaginationItem>

        {currentPage > pagesAround + 1 && renderEllipsis()}

        {paginationItems.map((pageNum) => (
          <PaginationItem
            key={pageNum}
            active={pageNum === currentPage}
            onClick={() => handlePageChange(pageNum)}
          >
            <PaginationLink>{pageNum}</PaginationLink>
          </PaginationItem>
        ))}

        {currentPage < pagesCount - pagesAround && renderEllipsis()}

        <PaginationItem
          disabled={isDisabled(currentPage + 1)}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          <PaginationLink>
            <Icon iconName="chevron-right-regular" />
          </PaginationLink>
        </PaginationItem>
        <PaginationItem
          disabled={isDisabled(currentPage + 1)}
          onClick={() =>
            currentPage !== pagesCount && handlePageChange(pagesCount)
          }
        >
          <PaginationLink>
            <Icon iconName="double-chevron-right-regular" />
          </PaginationLink>
        </PaginationItem>
      </Pagination>
    );
  },
);

export const TablePagination = React.memo(TablePaginationComp);
